import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { api, loadSession, onSessionExpired, saveSession } from '../api/client';
import type { AuthResult, Profile, Role } from '../api/types';

interface AuthState {
  profile: Profile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  /** true si el rol del usuario está entre los indicados. */
  hasRole: (...roles: Role[]) => boolean;
  /** Permiso efectivo sobre un router concreto. SUPER_ADMIN siempre puede. */
  canOnRouter: (routerId: string, permission: 'read' | 'create' | 'delete') => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(() => loadSession() !== null);
  const queryClient = useQueryClient();

  const loadProfile = useCallback(async () => {
    if (!loadSession()) {
      setProfile(null);
      setLoading(false);
      return;
    }
    try {
      setProfile(await api.get<Profile>('/auth/me'));
    } catch {
      // Un token guardado que ya no vale: se limpia y se pide login de nuevo.
      saveSession(null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  // El cliente HTTP avisa cuando la renovación falla; la sesión se cierra sin esperar a
  // que el usuario pulse nada.
  useEffect(
    () =>
      onSessionExpired(() => {
        setProfile(null);
        queryClient.clear();
      }),
    [queryClient],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await api.post<AuthResult>('/auth/login', { email, password });
      saveSession({ accessToken: result.accessToken, refreshToken: result.refreshToken });
      setProfile(await api.get<Profile>('/auth/me'));
    },
    [],
  );

  const logout = useCallback(async () => {
    const session = loadSession();
    if (session) {
      // Revocar en el servidor es lo que invalida el refresh token de verdad; borrar el
      // localStorage solo lo esconde. Si la petición falla se cierra igual en local.
      await api.post('/auth/logout', { refreshToken: session.refreshToken }).catch(() => undefined);
    }
    saveSession(null);
    setProfile(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<AuthState>(() => {
    const hasRole = (...roles: Role[]) => (profile ? roles.includes(profile.role) : false);

    return {
      profile,
      loading,
      login,
      logout,
      refreshProfile: loadProfile,
      hasRole,
      canOnRouter: (routerId, permission) => {
        if (!profile) return false;
        if (profile.role === 'SUPER_ADMIN') return true;
        const access = profile.routerAccess.find((entry) => entry.routerId === routerId);
        if (!access) return false;
        if (permission === 'create') return access.canCreate;
        if (permission === 'delete') return access.canDelete;
        return true;
      },
    };
  }, [profile, loading, login, logout, loadProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return context;
}
